import { useState } from 'react';
import { Bot } from '../../../../icons';
import { Avatar, TaskStatusIcon } from '../../../../components/shared';
import { listMyRooms } from '../../../../api/chat';
import { TASK_STATUS_META, TASK_PRIORITY_META, type TaskStatus, type TaskPriority } from '../../../../api/tasks';
import type { ChatRoomDto } from '../../../../types/chat';
import type { DocumentStatus } from '../../../../types/approval';
import { useWidgetData } from '../../../../hooks/useWidgetData';
import { useAuthStore } from '../../../../store/authStore';
import { useChatStore } from '../../../../store/chatStore';
import { useMenuGate } from '../../../../lib/menu-gate';
import { WidgetCard } from '../WidgetCard';
// 마감 한 줄 — 「오늘 마감 D-0 · 2일 지남 · 9/30 D-7」. 오늘을 세는 곳은 그 파일 하나다.
import { dueLabel } from '../due-label';
// 상대 시각 — 「방금 · N분 전 · N시간 전 · M/D」. 알림 위젯과 같은 글자를 쓴다.
import { agoText } from '../relative-time';
// 마지막 메시지 미리보기 글자 — 채팅방 목록과 «같은» 판정을 쓴다.
import { chatPreview } from '../../../../lib/chat-preview';
import { useWidgetActions } from '../widget-context';
import { useDraftedApprovals, useInbox, useMyTasks } from '../widget-queries';
// 대시보드 시안 v2 V3a (2026-09-16) — 보이는 항목 · 선택지를 읽는 판. 깃발이 꺼져 있으면 아래 옛 판 그대로.
import { DASHBOARD_V2_ENABLED } from '../../../../lib/layout-shell';
import { ApprovalWidgetV2 } from './v2/ApprovalWidgetV2';
import { MailWidgetV2 } from './v2/MailWidgetV2';

const DOC_STATUS_META: Record<DocumentStatus, { label: string; cls: string }> = {
  DRAFT: { label: '임시저장', cls: '' },
  IN_PROGRESS: { label: '진행중', cls: 'dw-chip--new' },
  APPROVED: { label: '완료', cls: 'dw-chip--ok' },
  REJECTED: { label: '반려', cls: 'dw-chip--reject' },
  RECALLED: { label: '회수', cls: '' },
};

/** 전자결재 진행현황 위젯 — 깃발(v2)이면 설정을 읽는 판, 아니면 옛 판. */
export function ApprovalWidget() {
  return DASHBOARD_V2_ENABLED ? <ApprovalWidgetV2 /> : <ApprovalWidgetLegacy />;
}

/** 전자결재 진행현황 위젯 (#2a) — 내가 올린 문서의 결재선 진행 상태. */
function ApprovalWidgetLegacy() {
  const { go } = useWidgetActions();
  /* ★ 메뉴 게이트 (2026-09-15) — 대시보드에서 이 위젯만 없었다. 전자결재를 끄면 다른 위젯처럼
       요청을 접고 「메뉴 관리에서 켜세요」 안내를 띄운다(`WidgetCard` 가 링크도 숨긴다). */
  const gate = useMenuGate('approval', '전자결재');
  const { data, loading, error } = useDraftedApprovals(gate.enabled);
  const rows = data ?? [];

  /* ★ 「상신한 문서」가 가는 곳은 **기안 문서함**이다 (2026-08-31 · 결재에 주소가 생겼다) —
       종전에는 결재 뿌리로만 보내 그 함을 다시 찾아 눌러야 했다. */
  return (
    <WidgetCard
      title="전자결재 진행현황"
      link={{ label: '더보기', onClick: () => go('/approval/drafts') }}
      loading={loading}
      error={error}
      empty={rows.length === 0 ? '상신한 문서가 없습니다' : null}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      <div className="dw-rows">
        {rows.slice(0, 3).map((doc) => {
          const meta = DOC_STATUS_META[doc.status] ?? { label: doc.status, cls: '' };
          const total = Math.max(1, doc.totalSteps);
          const done = Math.min(doc.currentStep, total);
          return (
            <button type="button" key={doc.id} className="dw-appr" onClick={() => go('/approval/drafts')}>
              <span className="dw-appr__top">
                <span className="dw-appr__t" title={doc.title}>{doc.title}</span>
                <span className={`dw-chip ${meta.cls}`}>{meta.label}</span>
              </span>
              <span className="dw-appr__line" aria-hidden>
                {Array.from({ length: total }, (_, i) => (
                  <span key={i} className="dw-appr__step">
                    {i > 0 && <span className={`dw-appr__seg${i <= done ? ' is-done' : ''}`} />}
                    <span className={`dw-appr__node${i < done ? ' is-done' : ''}`} />
                  </span>
                ))}
              </span>
              <span className="dw-appr__s">결재선 {done}/{total}</span>
            </button>
          );
        })}
      </div>
    </WidgetCard>
  );
}

/** 메일 위젯 — 깃발(v2)이면 설정을 읽는 판, 아니면 옛 판. */
export function MailWidget() {
  return DASHBOARD_V2_ENABLED ? <MailWidgetV2 /> : <MailWidgetLegacy />;
}

/** 메일 위젯 (#2b) — 받은 메일함 최근분 + 안읽음 수. */
function MailWidgetLegacy() {
  const { go } = useWidgetActions();
  const gate = useMenuGate('mail', '메일');
  const { data, loading, error } = useInbox(gate.enabled);
  const all = data ?? [];
  const unread = all.filter((m) => !m.isRead).length;

  /**
   * 시안 화면 L ④ — **거르개 셋**(안 읽음 · 전체 · 중요).
   *
   * ★★★ 서버를 다시 부르지 않는다 — 이미 받아 둔 목록을 «거르는» 것뿐이다. 위젯 하나가
   *     거르개를 누를 때마다 왕복하면 대시보드가 통째로 느려진다.
   * ★ 기본은 「안 읽음」이다 — 그것이 이 위젯을 보는 이유다.
   */
  const [filter, setFilter] = useState<'unread' | 'all' | 'star'>('unread');
  const rows = all.filter((m) => (filter === 'unread' ? !m.isRead : filter === 'star' ? m.isStarred : true));

  return (
    <WidgetCard
      title="메일"
      tail={unread > 0 ? <span className="dw-chip dw-chip--new">안읽음 {unread}</span> : null}
      link={{ label: '메일함', onClick: () => go('/mail') }}
      loading={loading}
      error={error}
      /* ★ 빈 상태는 «전체» 로 판단한다 — 거르개 때문에 카드가 통째로 사라지면 거르개를 못 되돌린다. */
      empty={all.length === 0 ? '받은 메일이 없습니다' : null}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      <div className="wi-mail__top">
        <span className="wi-mail__cnt">{unread}<span>안 읽음</span></span>
        <span className="wi-mail__filters">
          {([['unread', '안 읽음'], ['all', '전체'], ['star', '중요']] as const).map(([k, label]) => (
            <button
              key={k} type="button"
              className={`wi-mail__f${filter === k ? ' is-on' : ''}`}
              aria-pressed={filter === k}
              onClick={() => setFilter(k)}
            >
              {label}
            </button>
          ))}
        </span>
      </div>
      <div className="dw-rows">
        {rows.length === 0 && (
          <p className="dw-note">
            {filter === 'unread' ? '안 읽은 메일이 없습니다' : filter === 'star' ? '중요 표시한 메일이 없습니다' : '받은 메일이 없습니다'}
          </p>
        )}
        {rows.slice(0, 5).map((m) => (
          <button type="button" key={m.id} className="dw-row dw-row--stack" onClick={() => go('/mail')}>
            <span className={`dw-row__bullet${m.isRead ? '' : ' dw-row__bullet--on'}`} aria-hidden />
            <span className="dw-row__main">
              <span className={`dw-row__text${m.isRead ? ' dw-row__text--read' : ''}`} title={m.subject}>{m.subject}</span>
              <span className="dw-row__sub">{m.sender?.name || m.sender?.email || ''}</span>
            </span>
          </button>
        ))}
      </div>
    </WidgetCard>
  );
}

/** 내 태스크 위젯 — 담당자가 나인 미완료 태스크(우선순위·마감 순). */
export function TasksWidget() {
  const { go } = useWidgetActions();
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  // 레지스트리의 프로젝트/할일 메뉴는 menu_key='task' (url 은 /project 로 바뀌었어도 키는 유지).
  const gate = useMenuGate('task', '프로젝트');
  const { data, loading, error } = useMyTasks(myId, gate.enabled);
  const rows = data ?? [];

  return (
    <WidgetCard
      title="내 태스크"
      /* 시안의 머리 — 「진행 중 n」. 세는 것은 **지금 그리는 줄**이라 새 요청이 없다. */
      tail={rows.length > 0 ? <>진행 중 <b className="dw-num">{rows.length}</b></> : null}
      link={{ label: '전체 보기', onClick: () => go('/task') }}
      loading={loading}
      error={error}
      empty={rows.length === 0 ? '진행 중 태스크가 없습니다' : null}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      <div className="dw-rows">
        {rows.slice(0, 4).map((t) => {
          const status = TASK_STATUS_META[t.status as TaskStatus];
          const priority = TASK_PRIORITY_META[t.priority as TaskPriority];
          const due = dueLabel(t.dueDate);
          return (
            <button type="button" key={t.id} className="dw-row dw-row--stack" onClick={() => go('/task')}>
              {priority && <span className="dw-prio" style={{ background: priority.color }}>{priority.label}</span>}
              <span className="dw-row__main">
                <span className="dw-row__text" title={t.title}>{t.title}</span>
                <span className="dw-row__sub">
                  <TaskStatusIcon status={t.status as TaskStatus} size={11} />
                  {/* 마감은 시안처럼 «남은 날»로 — 셈은 `due-label.ts` 한 곳에서 한다(자정 경계). */}
                  {status?.label} · {t.projectName}
                  {due && <span className={`dw-due dw-due--${due.tone}`}> · {due.text}</span>}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </WidgetCard>
  );
}

/**
 * 최근 대화 위젯 — 최근 활동 순 채팅방.
 *
 * 2026-09-16 에 셋을 더했다. **셋 다 이미 응답에 실려 오던 값**이고, 화면이 안 쓰고 있었다.
 *
 * ★★★ **마지막 대화 시각** (`lastMessageAt`). 줄이 「마지막 대화 순」으로 서 있는데
 *     **그 기준이 화면에 안 보였다.** 게시판 이전 판(`RecentChatsWidget`)에는 있던 것이다.
 * ★★★ **진짜 얼굴** (`memberPreview`). 종전에는 `userId={0}` 으로 그려 **아무의 사진도
 *     안 나왔다**(이름 첫 글자만). 1:1 대화는 상대의 사진을 띄운다.
 *     ★ 사진이 없는 사람은 **종전처럼 첫 글자**다 — 빈 동그라미로 바꾸지 않는다.
 *     ★ 여럿 있는 방과 AI 방은 **손대지 않는다**(방 이름 첫 글자 · 글리프 그대로).
 * ★★ **제목 옆 안 읽음 수.** 줄마다 붙는 수는 있었는데 **합이 없었다.** 네 줄만 그리므로
 *     **다섯 번째 방의 안 읽음은 카드에서 사라진다** — 합이 있으면 그 사실이 드러난다.
 *     ⚠ 이 수는 **받아 온 20건의 합**이다. 그래서 「전사」·「전체」처럼 **범위를 주장하는 말을
 *       붙이지 않는다**(2026-09-16 사용자 결정 — 숫자만). 숫자만 두면 아무것도 주장하지 않는다.
 */
export function ChatsWidget() {
  const { go } = useWidgetActions();
  const setActiveChat = useChatStore((s) => s.setActiveChat);
  const myId = useAuthStore((s) => s.user?.id);
  const gate = useMenuGate('chat', '채팅');
  const { data, loading, error } = useWidgetData<ChatRoomDto[]>(
    'chat:rooms',
    async () => (await listMyRooms({ limit: 20 })).rooms ?? [],
    { enabled: gate.enabled },
  );
  const rows = data ?? [];

  const open = (roomId: string) => { setActiveChat(roomId); go('/chat'); };

  /** 받아 온 방 전부의 안 읽음 합 — 그리는 네 줄만이 아니다. */
  const unreadTotal = rows.reduce((n, r) => n + (r.unreadCount ?? 0), 0);

  /**
   * 1:1 대화의 «상대» — 나를 뺀 한 사람.
   * 못 고르면 `null` 이고, 그때는 종전대로 방 이름으로 그린다(지어내지 않는다).
   */
  const partnerOf = (room: ChatRoomDto) => {
    if (room.memberCount !== 2) return null;
    const mine = myId != null ? String(myId) : null;
    return (room.memberPreview ?? []).find((m) => m.userId !== mine) ?? null;
  };

  return (
    <WidgetCard
      title="최근 대화"
      tail={unreadTotal > 0 ? <span className="dw-chip dw-chip--new">{unreadTotal}</span> : null}
      link={{ label: '전체 보기', onClick: () => go('/chat') }}
      loading={loading}
      error={error}
      empty={rows.length === 0 ? '대화가 없습니다' : null}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      <div className="dw-rows">
        {rows.slice(0, 4).map((room) => {
          const partner = partnerOf(room);
          const pv = chatPreview(room.lastMessagePreview, { myUserId: myId == null ? null : String(myId), isDirect: room.type === 'direct' });
          return (
            <button type="button" key={room.id} className="dw-row dw-row--stack" onClick={() => open(room.id)}>
              {room.memberCount === 1
                ? <span className="dw-row__ai" aria-hidden><Bot size={14} /></span>
                : partner
                  ? <Avatar userId={Number(partner.userId)} name={partner.name} src={partner.avatarUrl} size="xs" />
                  : <Avatar userId={0} name={room.name ?? '대화'} size="xs" />}
              <span className="dw-row__main">
                <span className="dw-row__text" title={room.name ?? ''}>{room.name ?? '이름 없는 대화'}</span>
                {/*
                  ★ 마지막 메시지 — 무슨 글자로 보일지는 `lib/chat-preview` 한 곳이 정한다.
                    채팅방 목록도 같은 것을 쓴다(같은 방이 두 화면에서 다른 말을 하지 않게).
                  ⚠ 「—」는 «서버가 못 읽었다»이고 「첨부파일」은 «파일만 보냈다»이다 — 다른 일이다.
                */}
                <span className={`dw-row__sub wi-chat__pv wi-chat__pv--${pv.kind}`}>{pv.text}</span>
              </span>
              <span className="dw-row__meta">{agoText(room.lastMessageAt)}</span>
              {(room.unreadCount ?? 0) > 0 && <span className="dw-chip dw-chip--new">{room.unreadCount}</span>}
            </button>
          );
        })}
      </div>
    </WidgetCard>
  );
}
